<template>
  <div class="store-map">
    <client-only>
      <YandexMap
        :settings="settings"
        :coordinates="coordinates"
        :zoom="zoom"
        :controls="['zoomControl']"
        @created="onMapCreated"
      />
    </client-only>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { YandexMap } from 'vue-yandex-maps';
import type { MapSettings } from 'vue-yandex-maps/src/types';

const config = useRuntimeConfig();

const props = withDefaults(defineProps<{
  coordinates: number[];
  zoom?: number;
  address?: string;
  caption?: string;
}>(), {
  zoom: 15,
  address: '',
  caption: 'Шоу-рум MiaSofia',
});

const settings: MapSettings = {
  apiKey: config.public.yandexMapsApiKey,
  lang: 'ru_RU',
  coordorder: 'latlong',
  version: '2.1',
};

const placemarkSvg = `
  <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle class="map-circle-placemark__outline" cx="13" cy="13" r="13" fill="currentColor"></circle>
    <circle cx="13" cy="13" r="10" fill="white"></circle>
    <circle cx="13" cy="13" r="7" fill="currentColor"></circle>
  </svg>
`;

let map: any = null;
let placemark: any = null;

const buildIconLayout = () => {
  const html = `
    <div class="map-circle-placemark">
      ${placemarkSvg}
      <div class="map-circle-placemark__caption">${props.caption}</div>
    </div>
  `;

  return ymaps.templateLayoutFactory.createClass(html);
};

const setBalloonContent = (address: string) => {
  placemark?.properties.set('balloonContent', `<div class="map-widget-content-view__frame">${address}</div>`);
};

const onMapCreated = (mapInstance: any) => {
  map = mapInstance;

  placemark = new ymaps.Placemark(
    props.coordinates,
    { balloonContent: `<div class="map-widget-content-view__frame">${props.address}</div>` },
    {
      iconLayout: buildIconLayout(),
      iconShape: { type: 'Circle', coordinates: [13, 13], radius: 13 } as any,
      iconOffset: [-13, -13],
      hideIconOnBalloonOpen: false,
    }
  );

  map.geoObjects.add(placemark);
};

watch(() => props.coordinates, (coords) => {
  placemark?.geometry?.setCoordinates(coords);
});

watch(() => props.address, (address) => {
  setBalloonContent(address);
});
</script>

<style scoped lang="scss">
.store-map {
  position: absolute;
  inset: 0;
}

.store-map :deep(.yandex-container) {
  width: 100%;
  height: 100%;
}

.store-map :deep(.map-circle-placemark) {
  position: relative;
  display: flex;
  align-items: center;
  color: rgb(237, 69, 67);

  svg {
    display: block;
    flex-shrink: 0;
  }
}

.store-map :deep(.map-circle-placemark__caption) {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  width: max-content;
  flex-shrink: 0;
  max-width: 250px;
  padding: 0 8px;
  border-radius: 999px;
  background: hsla(0, 0%, 100%, 0.85);
  box-sizing: border-box;
  color: var(--text-color-text);
  margin-left: 2px;
  font-size: 13px;
  line-height: 26px;
}

.store-map :deep(.map-widget-content-view__frame) {
  font-size: 13px;
  line-height: 1.4;
}
</style>
